/**
 * Structured numbers behind one recommendation candidate.
 *
 * The product rule is that a candidate is explained with numbers and never with
 * prose, so this panel renders coverage counts, per-participant travel times,
 * the time window, the last-transit check, weather impact and the scoring
 * metadata. It intentionally renders no "reason" sentence.
 */

import { useTranslation } from 'react-i18next'

import type { ActivityKey } from '@shared/activities'
import { DECISION_DIMENSIONS, DECISION_DIMENSION_KEYS } from '@shared/dimensions'
import type { RecommendationDetails } from '@shared/types'

import {
  activityLabelKey,
  cn,
  formatDuration,
  formatProbability,
  formatScore,
  joinList,
  modeLabelKey,
} from '../../lib/format'
import { currentLocale } from '../../lib/i18n'
import { Badge } from '../ui/badge'
import { Separator } from '../ui/primitives'

/**
 * Constraint identifiers the engine reports, mapped to label keys.
 *
 * Unknown identifiers are dropped instead of being rendered raw: the panel must
 * never show an internal name to a participant.
 */
const CONSTRAINT_LABEL_KEYS: Record<string, string> = {
  poi_coverage: 'recommend.constraintPoiCoverage',
  travel_time: 'recommend.constraintTravelTime',
  time_window: 'recommend.constraintTimeWindow',
  last_transit: 'recommend.constraintLastTransit',
}

export interface RecommendationDetailsPanelProps {
  details: RecommendationDetails
  className?: string
}

/**
 * Why each dropped activity is missing, read from the two stored facts.
 *
 * The coverage counts say which activities the district has no place for, and
 * anything else that the plan dropped could only have been dropped by the shared
 * window (a covered, feasible activity is never left out). Reading the cause here
 * rather than assuming the time window is what keeps the plan notice, the time
 * window notice and the hard-constraint badges saying the same thing.
 *
 * Exported because the round page states the same cause next to the plan alert:
 * one definition of "why" is what stops the two surfaces from disagreeing.
 */
export function droppedActivityCauses(details: RecommendationDetails): {
  withoutPlaces: ActivityKey[]
  withoutTime: ActivityKey[]
} {
  const coverageByActivity = new Map(details.poiCoverage.map((entry) => [entry.activity, entry]))
  const dropped = details.plan?.droppedActivities ?? []
  const withoutPlaces = dropped.filter((activity) => {
    const entry = coverageByActivity.get(activity)
    // A row the coverage list does not describe proves nothing about the
    // district, so it is never reported as "no places": rounds stored before the
    // plan existed simply leave the cause unstated.
    return entry !== undefined && entry.poiCount < 1
  })
  // Everything else the district could host, so the shared window is what left it
  // out. A row with no coverage entry at all falls here rather than being claimed
  // as a place problem the data does not prove.
  const withoutTime = dropped.filter((activity) => !withoutPlaces.includes(activity))

  return { withoutPlaces, withoutTime }
}

/**
 * Dimension rows in the order the design lists them.
 *
 * They come from the shared definition rather than from a local list, so a
 * dimension added to the engine appears here without a second edit.
 */
const DIMENSION_ROWS = DECISION_DIMENSION_KEYS.map((key) => DECISION_DIMENSIONS[key])

export function RecommendationDetailsPanel({
  details,
  className,
}: RecommendationDetailsPanelProps) {
  const { t } = useTranslation()
  const locale = currentLocale()

  const confidence = formatProbability(details.scoring.confidence)
  const sourceLabel =
    details.scoring.source === 'jev'
      ? t('recommend.scoringSourceJev')
      : t('recommend.scoringSourceFallback')
  const availability =
    details.timeWindow.availableMinutes === null
      ? '—'
      : formatDuration(details.timeWindow.availableMinutes, locale)

  // Each drop is attributed to the district or to the shared window, so the two
  // notices below never blame each other.
  const { withoutPlaces: droppedWithoutPlaces, withoutTime: droppedWithoutTime } =
    droppedActivityCauses(details)

  return (
    <div className={cn('flex flex-col gap-3 text-sm', className)}>
      <div className="flex flex-wrap gap-1.5">
        <Badge variant={details.scoring.source === 'jev' ? 'secondary' : 'warning'}>
          {t('recommend.scoringSource')}: {sourceLabel}
        </Badge>
        <Badge variant="outline">
          {t('recommend.score', { score: formatScore(details.scoring.score) })}
        </Badge>
        {confidence ? (
          <Badge variant="outline">{t('recommend.confidence', { confidence })}</Badge>
        ) : null}
      </div>

      <Separator />

      {/* The plan is stated before the raw counts: a member has to be able to see
          that the round dropped an activity without reading every coverage row. */}
      {details.plan ? (
        <section className="flex flex-col gap-1">
          <h4 className="text-xs font-medium">{t('recommend.planTitle')}</h4>
          <div className="flex flex-wrap gap-1.5">
            {details.plan.activities.map((activity) => (
              <Badge key={activity} variant="secondary">
                {t(activityLabelKey(activity))}
              </Badge>
            ))}
          </div>
          {details.plan.degraded ? (
            <p className="text-destructive text-xs">
              {t('recommend.planDropped', {
                activities: joinList(
                  details.plan.droppedActivities.map((activity) => t(activityLabelKey(activity))),
                  t,
                ),
              })}
            </p>
          ) : null}
          {/* The cause of each drop, stated next to the dropped list so the time
              window section is never blamed for a place problem, nor the other way
              round. */}
          {droppedWithoutPlaces.length > 0 ? (
            <p className="text-destructive text-xs">
              {t('recommend.planDroppedNoPlaces', {
                activities: joinList(
                  droppedWithoutPlaces.map((activity) => t(activityLabelKey(activity))),
                  t,
                ),
              })}
            </p>
          ) : null}
          {droppedWithoutTime.length > 0 ? (
            <p className="text-destructive text-xs">
              {t('recommend.planDroppedNoTime', {
                activities: joinList(
                  droppedWithoutTime.map((activity) => t(activityLabelKey(activity))),
                  t,
                ),
              })}
            </p>
          ) : null}
        </section>
      ) : null}

      <Separator />

      <section className="flex flex-col gap-1">
        <h4 className="text-xs font-medium">{t('recommend.poiCoverage')}</h4>
        <ul className="flex flex-col gap-1.5">
          {details.poiCoverage.map((entry) => (
            <li key={entry.activity} className="flex flex-col gap-0.5">
              <span className="flex flex-wrap items-baseline gap-1.5">
                <span>
                  {t(activityLabelKey(entry.activity))} ·{' '}
                  {t('recommend.poiCount', { count: entry.poiCount })}
                </span>
                {/* The requirement is stated as who asked for it rather than as a
                    bare "required": next to a place count, "required" was read as
                    describing the places below instead of the activity itself. */}
                <Badge variant={entry.required ? 'default' : 'outline'}>
                  {entry.required
                    ? t('recommend.coverageRequired')
                    : t('recommend.coverageOptional', { count: entry.demand })}
                </Badge>
                {/* What the district has and what the plan uses are different
                    facts, so the second is stated rather than inferred. It only
                    exists once the round carried a plan. */}
                {details.plan ? (
                  <Badge variant={entry.delivered ? 'success' : 'warning'}>
                    {entry.delivered ? t('recommend.delivered') : t('recommend.notDelivered')}
                  </Badge>
                ) : null}
              </span>
              {entry.samplePois.length > 0 ? (
                <ul className="text-muted-foreground flex flex-wrap gap-x-3 text-xs">
                  {entry.samplePois.map((poi) => (
                    <li key={poi.id}>
                      {poi.name} · {t('recommend.distance', { meters: Math.round(poi.distanceMeters) })}
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      </section>

      <Separator />

      <section className="flex flex-col gap-1">
        <h4 className="text-xs font-medium">{t('recommend.travelTimes')}</h4>
        <ul className="flex flex-col gap-0.5">
          {details.travelTimes.map((entry) => (
            <li key={entry.departureId} className="flex flex-wrap gap-x-2">
              <span>{entry.label}</span>
              <span className="text-muted-foreground">
                {t('recommend.travelTimeValue', {
                  minutes: Math.round(entry.minutes),
                  mode: t(modeLabelKey(entry.mode)),
                })}
              </span>
              {entry.headcount > 1 ? (
                <span className="text-muted-foreground">
                  {t('recommend.travelTimeHeadcount', { count: entry.headcount })}
                </span>
              ) : null}
              {/* A round the organizer relaxed is the one case where the measured
                  time can sit above what the traveller themselves accepted, so the
                  declared limit and the overage are stated next to it. */}
              {Math.round(entry.minutes) > entry.declaredLimitMinutes ? (
                <span className="text-destructive text-xs">
                  {t('recommend.travelOverLimit', {
                    minutes: Math.round(entry.minutes) - entry.declaredLimitMinutes,
                  })}
                </span>
              ) : null}
              {/* The number above is measured; the estimate it was screened
                  against is shown only when the two disagree, so the basis of
                  the figure stays visible without cluttering an agreed one. */}
              {entry.estimatedMinutes !== null &&
              entry.estimatedMinutes !== Math.round(entry.minutes) ? (
                <span className="text-muted-foreground text-xs">
                  {t('recommend.travelEstimate', { minutes: entry.estimatedMinutes })}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
        <p className="text-muted-foreground text-xs">
          {t('recommend.travelStats', {
            max: Math.round(details.travelStats.maxMinutes),
            average: Math.round(details.travelStats.averageMinutes),
          })}
        </p>
        <p className="text-muted-foreground text-xs">{t('recommend.travelBasis')}</p>
      </section>

      <Separator />

      <section className="flex flex-col gap-1">
        <h4 className="text-xs font-medium">{t('recommend.timeWindow')}</h4>
        {/*
          One verdict, not two: the plan's own verdict and the declared list's are
          different facts, and printing both as separate sentences read as a
          contradiction ("fits" followed by "does not fit"). The three states are
          therefore mutually exclusive.
        */}
        {!details.timeWindow.ok ? (
          <p>{t('recommend.timeWindowFailed')}</p>
        ) : details.timeWindow.optionalFits ? (
          <p>{t('recommend.timeWindowOk')}</p>
        ) : (
          <p>{t('recommend.timeWindowPlanFits')}</p>
        )}
        <p className="text-muted-foreground text-xs">
          {t('recommend.timeWindowRequired', {
            duration: formatDuration(details.timeWindow.requiredMinutes, locale),
          })}
          {' · '}
          {t('recommend.timeWindowAvailable', { duration: availability })}
        </p>
      </section>

      <Separator />

      <section className="flex flex-col gap-1">
        <h4 className="text-xs font-medium">{t('recommend.lastTransit')}</h4>
        {/* Three states, not two: a bound that was never looked up is an unknown,
            and claiming the group can catch the last connection would state a
            check that never ran. */}
        <p>
          {details.lastTransit.checked
            ? details.lastTransit.ok
              ? t('recommend.lastTransitOk')
              : t('recommend.lastTransitFailed')
            : t('recommend.lastTransitUnchecked')}
        </p>
        {details.lastTransit.lastDeparture ? (
          <p className="text-muted-foreground text-xs">
            {details.lastTransit.anchorName
              ? t('recommend.lastDepartureFrom', {
                  station: details.lastTransit.anchorName,
                  time: details.lastTransit.lastDeparture,
                })
              : t('recommend.lastDeparture', { time: details.lastTransit.lastDeparture })}
            {details.lastTransit.estimated ? ` ${t('recommend.lastDepartureEstimated')}` : ''}
          </p>
        ) : null}
      </section>

      <Separator />

      <section className="flex flex-col gap-1">
        <h4 className="text-xs font-medium">{t('recommend.disclaimersTitle')}</h4>
        <ul className="text-muted-foreground flex flex-col gap-0.5 text-xs">
          <li>{t('disclaimer.lastDeparture')}</li>
          <li>{t('disclaimer.firstLeg')}</li>
        </ul>
      </section>

      <Separator />

      <section className="flex flex-col gap-1">
        <h4 className="text-xs font-medium">{t('recommend.dimensionsTitle')}</h4>
        <ul className="flex flex-col gap-0.5">
          {DIMENSION_ROWS.map((row) => (
            <li key={row.key} className="flex flex-wrap items-baseline gap-x-2">
              <span>{t(row.labelKey)}</span>
              <span className="text-muted-foreground">
                {formatScore(details.scoring.dimensions[row.key])}
              </span>
              <span className="text-muted-foreground text-xs">
                {t('recommend.dimensionWeight', { weight: `${Math.round(row.weight * 100)}%` })}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <Separator />

      <section className="flex flex-col gap-1">
        <h4 className="text-xs font-medium">{t('recommend.weatherTitle')}</h4>
        <p>{t(`weather.${details.weather.condition}`)}</p>
        {details.weather.outdoorActivitiesAffected.length > 0 ? (
          <p className="text-muted-foreground text-xs">
            {t('recommend.weatherAffected', {
              activities: details.weather.outdoorActivitiesAffected
                .map((activity) => t(activityLabelKey(activity)))
                .join(', '),
            })}
          </p>
        ) : null}
      </section>

      {details.hardConstraintsPassed.length > 0 ? (
        <>
          <Separator />
          <section className="flex flex-col gap-1">
            <h4 className="text-xs font-medium">{t('recommend.hardConstraints')}</h4>
            <div className="flex flex-wrap gap-1.5">
              {details.hardConstraintsPassed.map((constraint) => {
                const labelKey = CONSTRAINT_LABEL_KEYS[constraint]
                return labelKey ? (
                  <Badge key={constraint} variant="outline">
                    {t(labelKey)}
                  </Badge>
                ) : null
              })}
            </div>
          </section>
        </>
      ) : null}
    </div>
  )
}
